import { useCallback, useContext, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import { ButtonBar } from "@k8slens/lds";

import { useGetBusinessSubscriptions } from "src/hooks/useGetBusinessSubscriptions";
import { TipsContext } from "src/providers/tip-provider";
import { newSubscriptionForBusinesURLSearchParamsKey } from "src/hooks/useQueryNewSubscriptionForBusiness";
import { PlanCode, isAppIQPlan, isLensProPlan } from "src/services/plans";

import Button from "src/components/Button/TrackedButton";
import LinkButton from "src/components/Button/TrackedLinkButton";
import { BusinessContext } from "src/components/Business/Base";
import SubscriptionTable from "src/components/Business/SubscriptionTable";
import {
  targetId as addSubscriptionTipId,
  stepId as addSubscriptionStepId,
} from "src/components/Tips/add-lens-subscription";
import { targetId as manageSeatsTipId, stepId as manageSeatsStepId } from "src/components/Tips/manage-seats";

import styles from "src/components/Business/Business.module.css";
import { useConfirmDialog } from "src/components/ConfirmDialog/ConfirmDialog";

/**
 * The "root" page for the business, rendered at /business/:businessId
 */
const Business = () => {
  const history = useHistory();

  const { setStepIsVisible, skipStepIfActive } = useContext(TipsContext);
  const { businessId, business } = useContext(BusinessContext);
  const { businessSubscriptions, loading: loadingBusinessSubscriptions } = useGetBusinessSubscriptions(businessId);

  const filteredBusinessSubscriptions = useMemo(
    // Do not include AppIQ subscriptions in the table
    () => businessSubscriptions.filter((s) => !isAppIQPlan(s.planCode)),
    [businessSubscriptions],
  );

  const addSubscriptionDisabled = business?.external === true;

  const addManageSeatsDisabled = filteredBusinessSubscriptions.length === 0;
  const lensProSubscriptions = useMemo(
    () => filteredBusinessSubscriptions.filter((s) => isLensProPlan(s.planCode) && s.state !== "expired"),
    [filteredBusinessSubscriptions],
  );

  const { ConfirmDialog, confirm } = useConfirmDialog();

  useEffect(() => {
    if (!setStepIsVisible) {
      return;
    }

    setStepIsVisible(addSubscriptionStepId, !addSubscriptionDisabled);
  }, [addSubscriptionDisabled, setStepIsVisible]);

  const handleNewSubscriptions = useCallback(
    async (doConfirm: boolean) => {
      const confirmed = !doConfirm
        ? true
        : await confirm(
            "Create New Subscription?",
            <p>
              Your Lens Business ID already has a Lens Pro subscription associated with it.
              {/* eslint-disable-next-line max-len */}
              If you wish to purchase more seats you may{" "}
              <strong>modify the existing subscription to increase the number of seats</strong>. You can read more about
              adding and changing Lens subscriptions{" "}
              <a
                className="primary"
                target="_blank"
                href="https://docs.k8slens.dev/lens-id/lens-business-id/add-subscriptions/"
                rel="noreferrer"
              >
                here
              </a>
              .<p></p>
              Are you sure you want to create new subscription?
            </p>,
            { confirmText: "Create" },
          );

      if (confirmed) {
        if (skipStepIfActive) {
          skipStepIfActive(addSubscriptionStepId);
        }

        const planCode: PlanCode = "pro-yearly";

        history.push(`/subscribe/${planCode}/configure?${newSubscriptionForBusinesURLSearchParamsKey}=${businessId}`);
      }
    },
    [history, skipStepIfActive, businessId, confirm],
  );

  useEffect(() => {
    if (!setStepIsVisible) {
      return;
    }

    setStepIsVisible(manageSeatsStepId, !addManageSeatsDisabled);
  }, [addManageSeatsDisabled, setStepIsVisible]);

  const handleManageSeatsClick = useCallback(() => {
    if (skipStepIfActive) {
      skipStepIfActive(manageSeatsStepId);
    }
  }, [skipStepIfActive]);

  const createNewSubscriptionsLabel = "Create New Subscription";
  const manageSeatsLabel = "Manage Seats";

  // If the business has subscriptions, the CTA should be "Manage Seats"
  const cta = filteredBusinessSubscriptions.length > 0 ? manageSeatsLabel : createNewSubscriptionsLabel;

  return (
    <>
      <header className={styles.home}>
        <h2 className={clsx("lds-h2", styles.welcome)}>Manage your Lens Business ID</h2>
        <p>
          You can use Lens Business ID <strong>{`(${business?.handle || "-"})`}</strong> to manage subscriptions for
          your team.
        </p>
      </header>
      <section className={styles.subscriptions}>
        <h3 className={styles.tableTitle}>Purchased Subscriptions</h3>
        <SubscriptionTable
          businessSubscriptions={filteredBusinessSubscriptions}
          loading={loadingBusinessSubscriptions}
          business={business}
        />
        <ButtonBar type="grid" className={styles.buttonBar}>
          <LinkButton
            id={manageSeatsTipId}
            label={manageSeatsLabel}
            to={`/business/${businessId}/subscriptions`}
            onClick={handleManageSeatsClick}
            primary
            discreet={manageSeatsLabel !== cta}
            disabled={addManageSeatsDisabled}
          />
          {ConfirmDialog}
          <Button
            id={addSubscriptionTipId}
            label={createNewSubscriptionsLabel}
            primary
            discreet={createNewSubscriptionsLabel !== cta}
            onClick={() => handleNewSubscriptions(lensProSubscriptions.length > 0)}
            disabled={addSubscriptionDisabled}
          />
        </ButtonBar>
      </section>
    </>
  );
};

export default Business;
